import { useEffect, useRef, useState } from "react";
import {
  MdBlock,
  MdCalendarToday,
  MdDelete,
  MdEdit,
  MdForum,
  MdLink,
  MdMarkEmailRead,
  MdSync,
} from "react-icons/md";
import { FieldGroup, FormModal, Input, SelectField, Textarea } from "../../components/ui";
import { HabBadge, habColor } from "../../shared/habilitations";
import { statutRetraitColor } from "../../shared/statusColors";
import { formatDateFr } from "../../shared/dateFormat";
import { APP_FONT_FAMILY, COLORS, RADIUS } from "../../shared/theme";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";

const TYPES_COLLECTE = ["Formulaire web", "Inscription newsletter", "Création compte client", "Cookies / Traceurs", "Application mobile", "Enquête/Sondage", "Événement physique", "Partenaire tiers", "Autre"];
const MODALITES_RECUEIL = ["Opt-in simple (case à cocher)", "Double opt-in (confirmation email)", "Acceptation CGU/Politique", "Bandeau cookies", "Toggle ON/OFF", "Consentement oral (enregistré)", "Autre"];
const FINALITES_CONSENT = ["Newsletter marketing", "Prospection commerciale", "Cookies analytics", "Cookies publicitaires", "Partage avec partenaires", "Profilage", "Notifications push", "Géolocalisation", "Autre"];
const STATUT_RETRAIT = ["Reçue", "En cours de traitement", "Traitée", "Refusée"];
const ICON_ACTION_BUTTON = {
  width: 30,
  height: 30,
  borderRadius: RADIUS.sm,
  fontSize: 12,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};


const emptyPointCollecte = {
  nom: "", type: "Formulaire web", finalites: [], modaliteRecueil: "Opt-in simple (case à cocher)",
  libelleExact: "", preuveConsentement: "", outilsImpliques: "", responsableId: "",
  traitementROPA: "", observations: "", dateCreation: "", derniereMaj: "",
};

const emptyRetrait = {
  personne: "", dateReception: "", canal: "Lien désinscription email",
  pointsCollecteConcernes: "", consentementsConcernes: [], actionsMener: "",
  responsableId: "", statut: "Reçue", dateTraitement: "", observations: "",
};

export default function PageConsentement({ pointsCollecte, setPointsCollecte, retraits, setRetraits, personnes, traitements, searchHighlight }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formType, setFormType] = useState("point"); // "point" | "retrait"
  const nextIdPoint = useRef(1);
  const nextIdRetrait = useRef(1);
  const [form, setForm] = useState(null);
  const highlightRef = useRef(null);
  const retraitHighlightId =
    searchHighlight?.id != null && (!searchHighlight?.itemType || searchHighlight.itemType === "retrait")
      ? Number(searchHighlight.id)
      : null;

  useEffect(() => {
    if (retraitHighlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [retraitHighlightId]);
  useEffect(() => {
    const nums = pointsCollecte.map((p) => Number(p?.id)).filter((n) => Number.isFinite(n) && n >= 0);
    if (nums.length > 0) {
      nextIdPoint.current = Math.max(nextIdPoint.current, Math.max(...nums) + 1);
    }
  }, [pointsCollecte]);
  useEffect(() => {
    const nums = retraits.map((r) => Number(r?.id)).filter((n) => Number.isFinite(n) && n >= 0);
    if (nums.length > 0) {
      nextIdRetrait.current = Math.max(nextIdRetrait.current, Math.max(...nums) + 1);
    }
  }, [retraits]);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleArray = (k, val) => setForm(f => ({ ...f, [k]: f[k].includes(val) ? f[k].filter(v => v !== val) : [...f[k], val] }));

  const openNewPoint = () => { setFormType("point"); setEditing(null); setForm({ ...emptyPointCollecte }); setShowForm(true); };
  const openEditPoint = p => { setFormType("point"); setEditing(p); setForm({ ...p }); setShowForm(true); };
  const openNewRetrait = () => { setFormType("retrait"); setEditing(null); setForm({ ...emptyRetrait }); setShowForm(true); };
  const openEditRetrait = r => { setFormType("retrait"); setEditing(r); setForm({ ...r }); setShowForm(true); };

  const save = () => {
    if (formType === "point") {
      if (editing) setPointsCollecte(ps => ps.map(p => p.id === editing.id ? { ...form, id: editing.id } : p));
      else setPointsCollecte(ps => [...ps, { ...form, id: nextIdPoint.current++ }]);
    } else {
      if (editing) setRetraits(rs => rs.map(r => r.id === editing.id ? { ...form, id: editing.id } : r));
      else setRetraits(rs => [...rs, { ...form, id: nextIdRetrait.current++ }]);
    }
    setShowForm(false); setEditing(null); setForm(null);
  };

  const pOptions = [{ value: "", label: "— Sélectionner —" }, ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom} (${p.habilitation})` }))];
  const traitementOptions = [{ value: "", label: "— Aucun —" }, ...traitements.map(t => ({ value: t.nom, label: t.nom }))];
  const pointOptions = [{ value: "", label: "— Aucun —" }, ...pointsCollecte.map(p => ({ value: p.nom, label: p.nom }))];

  const statsRetraits = {
    total: retraits.length,
    enCours: retraits.filter(r => ["Reçue","En cours de traitement"].includes(r.statut)).length,
    traitees: retraits.filter(r => r.statut === "Traitée").length,
  };

  return (
    <PageShell>
      <PageHeader
        title="Gestion des consentements"
        subtitle="Cartographie des points de collecte + Retraits de consentement (art. 7 RGPD)"
      />

      <PageContent>
        <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>Points de collecte du consentement</div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>{pointsCollecte.length} points documentés — Cartographie des formulaires, cookies et canaux de recueil</div>
              </div>
              <button onClick={openNewPoint} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>+ Nouveau point de collecte</button>
            </div>

            {pointsCollecte.map(p => {
              const resp = personnes.find(r => r.id === p.responsableId || r.id === Number(p.responsableId));
              const hc = resp ? (habColor[resp.habilitation] || habColor["Autre"]) : null;
              return (
                <div key={p.id} style={{ background: COLORS.white, borderRadius: RADIUS.lg, border: `1.5px solid ${COLORS.border}`, padding: 18, marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: COLORS.textPrimary, marginBottom: 5 }}>{p.nom}</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#fdf4ff", color: "#7e22ce", border: "1px solid #e9d5ff", fontWeight: 600 }}>{p.type}</span>
                        <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#f0fdf4", color: "#15803d", border: "1px solid #86efac", fontWeight: 600 }}>{p.modaliteRecueil}</span>
                        {p.finalites.map(f => <span key={f} style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600 }}>{f}</span>)}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => openEditPoint(p)} style={{ ...ICON_ACTION_BUTTON, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent }}><MdEdit size={14} /></button>
                      <button onClick={() => setPointsCollecte(ps => ps.filter(x => x.id !== p.id))} style={{ ...ICON_ACTION_BUTTON, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626" }}><MdDelete size={14} /></button>
                    </div>
                  </div>
                  <div style={{ background: "#fdf4ff", borderRadius: RADIUS.md, padding: "10px 12px", marginBottom: 10, border: "1px dashed #e9d5ff" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#7e22ce", marginBottom: 4 }}>Libellé exact montré à l'utilisateur :</div>
                    <div style={{ fontSize: 12, color: "#374151", fontStyle: "italic" }}>"{p.libelleExact}"</div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 11, marginBottom: 8 }}>
                    <div><strong style={{ color: "#374151" }}>Preuve :</strong> <span style={{ color: COLORS.textSecondary }}>{p.preuveConsentement}</span></div>
                    <div><strong style={{ color: "#374151" }}>Outils :</strong> <span style={{ color: COLORS.textSecondary }}>{p.outilsImpliques}</span></div>
                    {p.traitementROPA && <div style={{ gridColumn: "span 2" }}><strong style={{ color: "#374151" }}>Lien ROPA :</strong> <span style={{ color: "#0c4a6e", display: "inline-flex", alignItems: "center", gap: 4 }}><MdLink size={13} /> {p.traitementROPA}</span></div>}
                  </div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, alignItems: "center" }}>
                    {resp && hc && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 22, height: 22, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text }}>{resp.prenom[0]}{resp.nom[0]}</div><span>{resp.prenom} {resp.nom}</span><HabBadge habilitation={resp.habilitation} /></div>}
                    {p.derniereMaj && <span style={{ color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdSync size={13} /> MAJ {formatDateFr(p.derniereMaj)}</span>}
                  </div>
                </div>
              );
            })}
          </>

          <div style={{ marginTop: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>Demandes de retrait de consentement</div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>{retraits.length} demandes — {statsRetraits.enCours} en cours, {statsRetraits.traitees} traitées</div>
              </div>
              <button onClick={openNewRetrait} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>+ Nouvelle demande de retrait</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 20 }}>
              {[
                { label: "Total demandes", val: statsRetraits.total, color: "#7e22ce", bg: "#fdf4ff", border: "#e9d5ff" },
                { label: "En cours", val: statsRetraits.enCours, color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
                { label: "Traitées", val: statsRetraits.traitees, color: "#065f46", bg: "#d1fae5", border: "#6ee7b7" },
              ].map(k => (
                <div key={k.label} style={{ background: k.bg, borderRadius: RADIUS.lg, padding: "14px 18px", border: `1.5px solid ${k.border}` }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.val}</div>
                  <div style={{ fontSize: 12, color: k.color, marginTop: 3, fontWeight: 600 }}>{k.label}</div>
                </div>
              ))}
            </div>

            {retraits.map(r => {
              const sc = statutRetraitColor[r.statut] || statutRetraitColor["Reçue"];
              const resp = personnes.find(p => p.id === r.responsableId || p.id === Number(r.responsableId));
              const hc = resp ? (habColor[resp.habilitation] || habColor["Autre"]) : null;
              const isRetraitHighlight =
                retraitHighlightId != null &&
                (r.id === retraitHighlightId || Number(r.id) === retraitHighlightId);
              return (
                <div
                  key={r.id}
                  ref={isRetraitHighlight ? highlightRef : null}
                  style={{
                    background: COLORS.white,
                    borderRadius: RADIUS.lg,
                    border: `1.5px solid ${isRetraitHighlight ? "#f59e0b" : COLORS.border}`,
                    padding: 18,
                    marginBottom: 12,
                    boxShadow: isRetraitHighlight ? "0 0 0 2px #fef3c7" : "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.textPrimary, marginBottom: 5 }}>{r.personne}</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#f1f5f9", color: "#374151", border: `1px solid ${COLORS.border}`, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><MdMarkEmailRead size={13} /> {r.canal}</span>
                        <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, fontWeight: 700 }}>{r.statut}</span>
                        {r.consentementsConcernes.map(c => <span key={c} style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#fef2f2", color: "#991b1b", border: "1px solid #fca5a5", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><MdBlock size={13} /> {c}</span>)}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => openEditRetrait(r)} style={{ ...ICON_ACTION_BUTTON, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent }}><MdEdit size={14} /></button>
                      <button onClick={() => setRetraits(rs => rs.filter(x => x.id !== r.id))} style={{ ...ICON_ACTION_BUTTON, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626" }}><MdDelete size={14} /></button>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}><strong>Actions :</strong> {r.actionsMener}</div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, alignItems: "center" }}>
                    {resp && hc && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 22, height: 22, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text }}>{resp.prenom[0]}{resp.nom[0]}</div><span>{resp.prenom} {resp.nom}</span><HabBadge habilitation={resp.habilitation} /></div>}
                    {r.dateReception && <span style={{ color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdCalendarToday size={13} /> Reçue le {formatDateFr(r.dateReception)}</span>}
                    {r.dateTraitement && <span style={{ color: "#065f46" }}>✓ Traitée le {formatDateFr(r.dateTraitement)}</span>}
                  </div>
                  {r.observations && <div style={{ marginTop: 8, padding: "6px 10px", background: "#f0fdf4", borderRadius: RADIUS.sm, fontSize: 11, color: "#15803d", display: "inline-flex", alignItems: "center", gap: 4 }}><MdForum size={13} /> {r.observations}</div>}
                </div>
              );
            })}
          </div>

        {/* Formulaire */}
        {showForm && form && (
          <FormModal
            title={formType === "point" ? (editing ? "Modifier le point de collecte" : "Nouveau point de collecte") : (editing ? "Modifier la demande de retrait" : "Nouvelle demande de retrait")}
            headerBackground="#7e22ce"
            maxWidth={700}
            footer={
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={() => { setShowForm(false); setForm(null); setEditing(null); }} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
                <button type="button" onClick={save} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
              </div>
            }
          >
                {formType === "point" ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Nom du point de collecte"><Input value={form.nom} onChange={set("nom")} placeholder="Ex : Formulaire newsletter site web" /></FieldGroup>
                    </div>
                    <FieldGroup label="Type"><SelectField value={form.type} onChange={set("type")} options={TYPES_COLLECTE} /></FieldGroup>
                    <FieldGroup label="Modalité de recueil"><SelectField value={form.modaliteRecueil} onChange={set("modaliteRecueil")} options={MODALITES_RECUEIL} /></FieldGroup>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Finalités">
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                          {FINALITES_CONSENT.map(f => {
                            const selected = form.finalites.includes(f);
                            return <button key={f} type="button" onClick={() => toggleArray("finalites", f)} style={{ padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: selected ? "#eff6ff" : "#f9fafb", color: selected ? "#1d4ed8" : "#6b7280", border: `1.5px solid ${selected ? "#bfdbfe" : COLORS.border}` }}>{f}</button>;
                          })}
                        </div>
                      </FieldGroup>
                    </div>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Libellé exact montré à l'utilisateur"><Textarea value={form.libelleExact} onChange={set("libelleExact")} rows={2} placeholder="Texte exact de la case à cocher, du bandeau cookies, etc." /></FieldGroup>
                    </div>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Preuve du consentement"><Textarea value={form.preuveConsentement} onChange={set("preuveConsentement")} rows={2} placeholder="Comment prouvez-vous le consentement ? Horodatage, IP, version CGU, logs…" /></FieldGroup>
                    </div>
                    <FieldGroup label="Outils/plateformes impliqués"><Input value={form.outilsImpliques} onChange={set("outilsImpliques")} placeholder="Ex : Mailchimp, OneTrust, PrestaShop…" /></FieldGroup>
                    <FieldGroup label="Responsable"><SelectField value={form.responsableId} onChange={set("responsableId")} options={pOptions} /></FieldGroup>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Lien vers traitement ROPA"><SelectField value={form.traitementROPA} onChange={set("traitementROPA")} options={traitementOptions} /></FieldGroup>
                    </div>
                    <FieldGroup label="Date de création"><Input type="date" value={form.dateCreation} onChange={set("dateCreation")} /></FieldGroup>
                    <FieldGroup label="Dernière MAJ"><Input type="date" value={form.derniereMaj} onChange={set("derniereMaj")} /></FieldGroup>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Observations"><Textarea value={form.observations} onChange={set("observations")} rows={1} /></FieldGroup>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Personne (nom + email)"><Input value={form.personne} onChange={set("personne")} placeholder="Ex : Marie Dupont (marie.dupont@example.com)" /></FieldGroup>
                    </div>
                    <FieldGroup label="Date de réception"><Input type="date" value={form.dateReception} onChange={set("dateReception")} /></FieldGroup>
                    <FieldGroup label="Canal"><SelectField value={form.canal} onChange={set("canal")} options={["Lien désinscription email", "Email DPO", "Formulaire web", "Téléphone", "Courrier", "Chat support"]} /></FieldGroup>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Point(s) de collecte concerné(s)"><SelectField value={form.pointsCollecteConcernes} onChange={set("pointsCollecteConcernes")} options={pointOptions} /></FieldGroup>
                    </div>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Consentement(s) retiré(s)">
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                          {FINALITES_CONSENT.map(f => {
                            const selected = form.consentementsConcernes.includes(f);
                            return <button key={f} type="button" onClick={() => toggleArray("consentementsConcernes", f)} style={{ padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: selected ? "#fef2f2" : "#f9fafb", color: selected ? "#991b1b" : "#6b7280", border: `1.5px solid ${selected ? "#fca5a5" : COLORS.border}` }}>{f}</button>;
                          })}
                        </div>
                      </FieldGroup>
                    </div>
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Actions à mener"><Textarea value={form.actionsMener} onChange={set("actionsMener")} rows={2} placeholder="Suppressions, mises à jour, notifications…" /></FieldGroup>
                    </div>
                    <FieldGroup label="Responsable traitement"><SelectField value={form.responsableId} onChange={set("responsableId")} options={pOptions} /></FieldGroup>
                    <FieldGroup label="Statut"><SelectField value={form.statut} onChange={set("statut")} options={STATUT_RETRAIT} /></FieldGroup>
                    <FieldGroup label="Date de traitement"><Input type="date" value={form.dateTraitement} onChange={set("dateTraitement")} /></FieldGroup>
                    <div />
                    <div style={{ gridColumn: "span 2" }}>
                      <FieldGroup label="Observations"><Textarea value={form.observations} onChange={set("observations")} rows={1} /></FieldGroup>
                    </div>
                  </div>
                )}
          </FormModal>
        )}
      </PageContent>
    </PageShell>
  );
}
